import { useAuthStore } from "../services/authStore";
import { useTranslation } from "../contexts/TranslationContext";
import PageShell from "../components/ui/PageShell";
import PageHeader from "../components/ui/PageHeader";
import LoadingState from "../components/ui/LoadingState";
import ErrorState from "../components/ui/ErrorState";
import EmptyState from "../components/ui/EmptyState";
import useNotificationStore from "../services/notificationStore";
import ExchangeRatesOverview from "./exchange-rates/ExchangeRatesOverview";
import ExchangeRateFilters from "./exchange-rates/ExchangeRateFilters";
import ExchangeRatesMatrix from "./exchange-rates/ExchangeRatesMatrix";
import ExchangeRatePairsTable from "./exchange-rates/ExchangeRatePairsTable";
import ExchangeRateSyncPanel from "./exchange-rates/ExchangeRateSyncPanel";
import useExchangeRatesPage from "./exchange-rates/useExchangeRatesPage";

const TABS = ["overview", "matrix", "pairs", "sync"];

export default function ExchangeRates() {
  const { user } = useAuthStore();
  const { t } = useTranslation();
  const showToast = useNotificationStore((state) => state.showToast);

  const page = useExchangeRatesPage({ user, showToast, t });

  if (!user?.is_admin) {
    return (
      <PageShell className="p-4">
        <div className="rounded-lg border border-outline bg-surface p-6 shadow-sm">
          <h3 className="text-lg font-semibold">
            {t("Access Denied", "Access Denied")}
          </h3>
          <p className="mt-2 text-sm text-muted-foreground">
            {t(
              "You are not authorised to manage exchange rates.",
              "You are not authorised to manage exchange rates.",
            )}
          </p>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell className="px-4 py-6 sm:px-6 lg:px-8">
      <PageHeader
        title={t("Exchange Rates", "Exchange Rates")}
        subtitle={t(
          "Inspect exchange-rate health, matrix coverage, provider failures, and manual synchronization status.",
          "Inspect exchange-rate health, matrix coverage, provider failures, and manual synchronization status.",
        )}
      />

      <nav
        className="flex gap-1 border-b border-outline"
        aria-label="Exchange rates tabs"
      >
        {TABS.map((tab) => {
          const isActive = page.activeTab === tab;
          return (
            <button
              key={tab}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => page.setActiveTab(tab)}
              className={`px-4 py-2 text-sm font-medium capitalize border-b-2 transition ${
                isActive
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              {tab === "sync" ? "Sync & Providers" : tab}
            </button>
          );
        })}
      </nav>

      <ExchangeRateFilters
        filters={page.filters}
        onChange={page.handleFilterChange}
        onApply={page.handleApplyFilters}
        onReset={page.handleResetFilters}
        availableBaseCurrencies={page.availableBaseCurrencies}
        availableQuoteCurrencies={page.availableQuoteCurrencies}
      />

      {page.activeTab === "overview" && (
        <>
          {page.overviewLoading && !page.overview ? (
            <LoadingState text="Loading overview..." />
          ) : null}
          {page.overviewError && !page.overview ? (
            <ErrorState
              text={page.overviewError}
              action={
                <button
                  type="button"
                  className="underline"
                  onClick={page.loadOverview}
                >
                  Retry
                </button>
              }
            />
          ) : null}
          {page.overview && (
            <ExchangeRatesOverview
              summary={page.overview.summary}
              autoSyncStatus={page.autoSyncStatus}
            />
          )}
          {!page.overviewLoading && !page.overviewError && !page.overview ? (
            <EmptyState
              title="No overview data"
              description="No summary data available yet."
            />
          ) : null}
        </>
      )}

      {page.activeTab === "matrix" ? (
        <ExchangeRatesMatrix
          matrix={page.matrix}
          loading={page.matrixLoading}
          error={page.matrixError}
          onRetry={page.loadMatrix}
          selectedCell={page.selectedCell}
          onCellSelect={page.setSelectedCell}
          sort={page.matrixSort}
          onSortChange={page.handleMatrixSortChange}
          onPageChange={page.handleMatrixPageChange}
          onPageSizeChange={page.handleMatrixPageSizeChange}
        />
      ) : null}

      {page.activeTab === "pairs" ? (
        <ExchangeRatePairsTable
          pairs={page.pairs}
          loading={page.pairsLoading}
          error={page.pairsError}
          onRetry={page.loadPairs}
          sort={page.pairsSort}
          onSortChange={page.handlePairsSortChange}
          onPageChange={page.handlePairsPageChange}
          onPageSizeChange={page.handlePairsPageSizeChange}
        />
      ) : null}

      {page.activeTab === "sync" ? (
        <ExchangeRateSyncPanel
          syncRows={page.sortedSyncStatus}
          loading={page.syncLoading}
          error={page.syncError}
          autoSyncStatus={page.autoSyncStatus}
          baseCurrency={page.baseCurrency}
          supportedCurrencies={page.supportedCurrencies}
          syncing={page.syncing}
          fullSyncProgress={page.fullSyncProgress}
          onBaseCurrencyChange={(value) =>
            page.setBaseCurrency(String(value).toUpperCase())
          }
          onReload={page.loadSyncStatus}
          onSync={page.handleManualSync}
          onFullSync={page.handleFullSync}
        />
      ) : null}

      {page.overviewError ||
      page.matrixError ||
      page.pairsError ||
      page.syncError ? (
        <div className="rounded-lg border border-warning/40 bg-warning/10 p-3 text-sm">
          Section-level errors detected. Other tabs remain available.
        </div>
      ) : null}
    </PageShell>
  );
}
